'use client';

import { FC, useEffect, useMemo, useState } from 'react';

import { ElasticUtils } from '@ravago/shared/next-data-access/client';
import { FeatureToggle, RoleBasedToggle, UnitSystem } from '@ravago/shared/page-data/models/config';
import {
   AutoSuggestQueryV2Args,
   AutoSuggestV2Response,
   BasicFacetWithTermsV2,
   FacetFilterTracking,
   facetLabelTranslationMappingV2,
   OverviewPageItem,
   ProductRequestType,
   ProductsQueryV2Response
} from '@ravago/shared/page-data/models/elastic';
import { ProductPortfolioNode } from '@ravago/shared/page-data/models/elements';
import { RendererGlobals } from '@ravago/shared/page-data/models/utils';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { BreakpointSwitch } from '@ravago/shared/radiance/components/breakpoint-switch/components/BreakpointSwitch/BreakpointSwitch';
import { Flyout } from '@ravago/shared/radiance/components/flyout/components/Flyout/Flyout';
import { Grid } from '@ravago/shared/radiance/components/grid/components/Grid/Grid';
import { GridCol } from '@ravago/shared/radiance/components/grid/components/GridCol/GridCol';
import { useBreakpoint } from '@ravago/shared/radiance/hooks/useBreakpoint/useBreakpoint';
import notification from '@ravago/shared/radiance/utils/notification/notification';
import { useTranslations } from '@ravago/shared/translations/client';

import { keepPreviousData, useQuery } from '@tanstack/react-query';

import isEqual from 'lodash/isEqual';

import { CorrelationService } from '../../../../services/tracking/correlation-token.service';
import { getLanguageCode } from '../../../../utils/locale/locale.utils';
import { TrackingUtils } from '../../../../utils/tracking/tracking.utils';
import { ProductCompareSelection } from '../../product-compare/components/product-compare-selection';
import { useProductPortfolioPreferences } from '../hooks/use-product-portfolio-preferences';
import { useProductPortfolioSearchParams } from '../hooks/use-product-portfolio-search-params';
import { AutoSuggestClientV2Request, SearchClientV2Request } from '../models/client-request.model';
import { FacetGroupStructureV2 } from '../models/facet-group.model';
import { buildQueryKey } from '../utils/build-query-key';
import { mapResponseToFacetGroups } from '../utils/v2-mappers-utils';

import styles from './product-portfolio.module.scss';
import { ProductPortfolioEmpty } from './ui/portfolio/product-portfolio-empty';
import { ProductPortfolioFiltersV2 } from './ui/portfolio/product-portfolio-filters-v2';
import { ProductPortfolioGrid } from './ui/portfolio/product-portfolio-grid';
import { ProductPortfolioSearchV2 } from './ui/portfolio/product-portfolio-search-v2';
import { ProductPortfolioTable } from './ui/portfolio/product-portfolio-table-view';
import { ProductPortfolioTabs } from './ui/portfolio/product-portfolio-tabs';

const AUTO_SUGGEST_SIZE = 30;

const getAutoSuggestResult = ({ search, correlationId, language, country }: AutoSuggestClientV2Request): Promise<AutoSuggestV2Response> => {
   const request: AutoSuggestQueryV2Args = {
      query: search.trim(),
      size: AUTO_SUGGEST_SIZE,
      language,
      country
   };

   return ElasticUtils.getAutosuggestV2Result(request, correlationId);
};

interface Props {
   baseRoutes?: {
      brand: string;
      caseStudy: string;
      product: string;
      market: string;
      technology: string;
   };
   relatedDocumentsAnchor?: string;
   node: ProductPortfolioNode;
   globals: RendererGlobals;
   allowListViewOption?: boolean;
   allowShowPhysicalProperties?: boolean;
   allowTechnicalPropertyFilters?: boolean;
   hasPublicLabelAndSigningsToggles?: boolean;
   hasSustainabilityLabels?: boolean;
   baseFacetFilters: BasicFacetWithTermsV2[];
   getSearchResults: (request: SearchClientV2Request) => Promise<ProductsQueryV2Response<OverviewPageItem>>;
   featureToggles: FeatureToggle[];
   roleBasedToggle: RoleBasedToggle;
   defaultCountry?: string;
   defaultUnitSystem: UnitSystem;
}

export const ProductPortfolioV2: FC<Props> = ({
   baseRoutes,
   relatedDocumentsAnchor,
   node,
   globals,
   allowListViewOption = false,
   allowShowPhysicalProperties = false,
   allowTechnicalPropertyFilters = false,
   hasPublicLabelAndSigningsToggles = false,
   hasSustainabilityLabels = false,
   baseFacetFilters,
   getSearchResults,
   featureToggles,
   roleBasedToggle,
   defaultCountry,
   defaultUnitSystem
}) => {
   const translate = useTranslations();

   const { desktop, phone } = useBreakpoint();

   const language = getLanguageCode(globals.locale);

   // Tracking state to know when to trigger tracking events
   const [trackedSearch, setTrackedSearch] = useState<string>('');
   const [trackFacetsAndSearchBeforeReset, setTrackFacetsAndSearchBeforeReset] = useState<
      { facets: BasicFacetWithTermsV2[]; search: string; page: number } | undefined
   >();
   const [trackFacetClick, setTrackFacetClick] = useState<FacetFilterTracking | undefined>(undefined);

   // Search & Filter State
   const {
      view: preferencesView,
      unitSystem: preferencesUnitSystem,
      columnSelection,
      saveView,
      saveUnitSystem,
      saveColumnSelection
   } = useProductPortfolioPreferences();

   const {
      tab: searchType,
      search,
      page,
      view,
      unitSystem,
      facetFilters: activeFacets,
      setTab,
      setSearch,
      setPage,
      setView,
      setUnitSystem,
      setFacets,
      resetFacets
   } = useProductPortfolioSearchParams({
      defaultCountry,
      defaultUnitSystem,
      defaultFacetFilters: baseFacetFilters
   });

   // Restore last-used view from session storage on mount
   useEffect(() => {
      if (!preferencesView || !!globals.searchParams?.view) return;
      setView(preferencesView, preferencesUnitSystem);
   }, []);

   const [autoSuggestSearch, setAutoSuggestSearch] = useState(search);
   const [autoSuggestExpanded, setAutoSuggestExpanded] = useState(false);
   const [expandedGroups, setExpandedGroups] = useState<Array<string>>([]);

   const canResetFilters = useMemo(() => !isEqual(baseFacetFilters, activeFacets), [baseFacetFilters, activeFacets]);

   const searchQuery = useQuery({
      queryKey: buildQueryKey({ facets: activeFacets, search, page, tab: searchType, view, unit: unitSystem }),
      queryFn: () =>
         getSearchResults({
            facets: activeFacets,
            search,
            page,
            searchType,
            view,
            language,
            unit: unitSystem,
            correlationId: CorrelationService.generateCorrelationId('dpcid')
         }),
      placeholderData: keepPreviousData,
      refetchOnMount: false,
      refetchOnWindowFocus: false
   });

   const totals = useMemo(
      () => ({
         product: searchQuery.data?.documentCounts.product,
         brand: searchQuery.data?.documentCounts.brand,
         caseStudy: searchQuery.data?.documentCounts.caseStudy
      }),
      [searchQuery.data]
   );

   const trackSearch = (allTotals: Record<ProductRequestType, number | undefined>) => {
      TrackingUtils.track('product-portfolio-search', {
         correlationId: CorrelationService.getCorrelationId('dpcid'),
         searchQuery: search,
         totalAmountOfItems: JSON.stringify(allTotals),
         activeFacets: JSON.stringify(TrackingUtils.translateFacetsV2([...activeFacets], translate)),
         language
      });
      setTrackedSearch(search);
   };

   const trackFilterReset = () => {
      if (!trackFacetsAndSearchBeforeReset) return;
      TrackingUtils.track('click_reset_all_filters', {
         correlationId: CorrelationService.getCorrelationId('dpcid'),
         searchQueryBeforeReset: trackFacetsAndSearchBeforeReset.search,
         facetsBeforeReset: JSON.stringify(TrackingUtils.translateFacetsV2([...trackFacetsAndSearchBeforeReset.facets], translate)),
         pageNumberBeforeReset: trackFacetsAndSearchBeforeReset.page.toString()
      });
      setTrackFacetsAndSearchBeforeReset(undefined);
   };

   const trackFacets = (allTotals: Record<ProductRequestType, number | undefined>) => {
      TrackingUtils.track('click_filter_facet', {
         correlationId: CorrelationService.getCorrelationId('dpcid'),
         selectedFilters: JSON.stringify(TrackingUtils.translateFacetsV2([...activeFacets], translate)),
         lastSelectedFilter: JSON.stringify({
            ...trackFacetClick,
            facet: trackFacetClick?.facet
               ? translate(facetLabelTranslationMappingV2.get(trackFacetClick.facet) ?? trackFacetClick.facet, undefined, true)
               : undefined
         }),
         totalAmountOfItems: JSON.stringify(allTotals)
      });
      setTrackFacetClick(undefined);
   };

   useEffect(() => {
      if (!searchQuery.data?.documentCounts) return;
      const allTotals = {
         product: searchQuery.data.documentCounts.product,
         brand: searchQuery.data.documentCounts.brand,
         caseStudy: searchQuery.data.documentCounts.caseStudy
      };
      // Only track the search when the app gives the user a result and when the search changes
      if (search !== trackedSearch) trackSearch({ ...allTotals });
      // Only track the facets when the app gives the user a result and when a facet is added
      if (trackFacetClick) trackFacets({ ...allTotals });
      // Track the reset of the filters
      if (trackFacetsAndSearchBeforeReset) trackFilterReset();
   }, [searchQuery.data]);

   const currentActiveCountryTerms: string[] = useMemo(() => {
      if (!activeFacets) return [];
      const countryFacet = activeFacets.find((facet) => facet.facetName === 'country');
      return countryFacet ? countryFacet.terms.flatMap((cf) => cf.key) : [];
   }, [activeFacets]);

   const autoSuggestQuery = useQuery({
      queryKey: ['auto-suggest', { search: autoSuggestSearch }],
      queryFn: async ({ signal }) => {
         await new Promise((resolve) => {
            setTimeout(resolve, 500);
         });
         if (signal.aborted) {
            return undefined;
         }
         return getAutoSuggestResult({
            search: autoSuggestSearch,
            correlationId: CorrelationService.generateCorrelationId('dpcid'),
            country: currentActiveCountryTerms,
            language
         });
      },
      enabled: () => autoSuggestSearch.length > 1 && autoSuggestExpanded
   });

   // UI State
   const [filterFlyoutOpen, setFilterFlyoutOpen] = useState(false);

   const facetGroups: FacetGroupStructureV2[] = useMemo(() => {
      if (!searchQuery.data) return [];
      return mapResponseToFacetGroups(searchQuery.data.facets, translate, false, allowTechnicalPropertyFilters);
   }, [allowTechnicalPropertyFilters, searchQuery.data, translate]);

   const filtersVisible = useMemo(
      () => activeFacets.length > 0 || (searchQuery.data && Object.keys(searchQuery.data.facets).length > 0),
      [activeFacets, searchQuery.data]
   );

   useEffect(() => {
      if (searchQuery.error) {
         notification(translate('products-overview-error'));
      }
   }, [searchQuery.error, language]);

   useEffect(() => {
      if (desktop) setFilterFlyoutOpen(false);
   }, [desktop]);

   const onSearchChange = (query: string) => {
      setAutoSuggestSearch(query);
   };

   const onSearch = (query: string) => {
      setSearch(query);
   };

   const onTabChange = (newTab: ProductRequestType) => {
      setExpandedGroups([]);
      setTab(newTab, preferencesView, preferencesUnitSystem);
   };

   const onPageChange = (page: number) => {
      setPage(page);
   };

   const onActiveColumnUpdate = (newActiveColumns: string[]) => {
      saveColumnSelection(newActiveColumns);
   };

   const onUpdateViewType = async (view: 'grid' | 'list') => {
      if (!allowListViewOption) return;
      saveView(view);
      const savedUnitSystem = view === 'list' ? preferencesUnitSystem ?? defaultUnitSystem : undefined;
      setView(view, savedUnitSystem);
   };

   const onUpdateUnitSystem = (newUnitSystem: UnitSystem) => {
      saveUnitSystem(newUnitSystem);
      setUnitSystem(newUnitSystem);
   };

   const facetChange = async (newFacets: BasicFacetWithTermsV2[], switchToList?: boolean) => {
      setFacets(newFacets);
      if (switchToList) {
         const savedUnitSystem = preferencesUnitSystem ?? defaultUnitSystem;
         setView('list', savedUnitSystem);
      }
   };

   const facetReset = () => {
      setTrackFacetsAndSearchBeforeReset({ facets: [...activeFacets], search, page });
      resetFacets();
   };

   const onExpand = (key: string, expanded: boolean) => {
      if (expanded) {
         setExpandedGroups([...expandedGroups, key]);
      } else {
         setExpandedGroups(expandedGroups.filter((group) => group !== key));
      }
   };

   const filters = searchQuery.data ? (
      <ProductPortfolioFiltersV2
         activeFacets={activeFacets}
         combinedFacetGroupStructure={facetGroups}
         canResetFacets={canResetFilters}
         filterFlyoutOpen={filterFlyoutOpen}
         language={language}
         locked={searchQuery.isPlaceholderData}
         unitSystem={unitSystem}
         expandedGroups={expandedGroups}
         onFacetsAdjust={(facets: BasicFacetWithTermsV2[], switchToList?: boolean) => facetChange(facets, switchToList)}
         onFacetReset={facetReset}
         onFacetClicked={(value) => setTrackFacetClick(value)}
         onExpandChange={onExpand}
         onCloseAll={() => setExpandedGroups([])}
         onFilterFlyoutClose={() => setFilterFlyoutOpen(false)}
      />
   ) : null;

   return (
      <>
         <Box gap="lg" direction="column">
            <Box gap="md" direction="column">
               <ProductPortfolioSearchV2
                  onAutoSuggestMenuChange={(value) => setAutoSuggestExpanded(value)}
                  autoSuggestExpanded={autoSuggestExpanded}
                  language={language}
                  numberOfFacets={activeFacets.length}
                  autoSuggestResult={autoSuggestQuery.data}
                  loading={autoSuggestQuery.isFetching}
                  baseRoutes={baseRoutes}
                  globals={globals}
                  activeFacets={activeFacets}
                  onFacetsChange={(facetFilters) => facetChange(facetFilters)}
                  onFilterFlyoutOpen={() => setFilterFlyoutOpen(true)}
                  onSearchChange={onSearchChange}
                  onSearch={onSearch}
                  data-testid="product-portfolio__search"
                  featureToggles={featureToggles}
               />
               <ProductPortfolioTabs
                  onTabChange={onTabChange}
                  switchView={onUpdateViewType}
                  searchType={searchType}
                  viewType={view}
                  allowListViewOption={allowListViewOption && !phone}
                  totals={totals}
                  features={featureToggles}
                  locked={searchQuery.isPlaceholderData}
                  unitSystem={unitSystem}
                  onUomSwitch={onUpdateUnitSystem}
                  allowShowPhyiscalProperties={allowShowPhysicalProperties}
                  listViewActive={view === 'list'}
               />
            </Box>

            {searchQuery.data && (
               <Grid useContainerQuery={!globals.isConsumer}>
                  {filtersVisible && (
                     <BreakpointSwitch includeViews={['desktop']}>
                        <GridCol spanDesktop={3} spanTablet={0} spanPhone={0}>
                           {filters}
                        </GridCol>
                     </BreakpointSwitch>
                  )}
                  <GridCol spanDesktop={filtersVisible ? 9 : 12} spanTablet={12} spanPhone={4} useContainerQuery={!globals.isConsumer}>
                     {roleBasedToggle.hasProductCompareFeatureToggle && searchType === 'product' && (
                        <Box spacing={{ bottom: 'lg' }}>
                           <ProductCompareSelection locale={globals.locale} />
                        </Box>
                     )}
                     {searchQuery.data.content.length === 0 ? (
                        <ProductPortfolioEmpty searchTerm={search} />
                     ) : (
                        <div
                           className={styles.productPortfolio__grid}
                           style={{
                              opacity: searchQuery.isPlaceholderData ? 0.5 : 1,
                              pointerEvents: searchQuery.isPlaceholderData ? 'none' : 'unset'
                           }}
                        >
                           {view === 'grid' || searchType !== 'product' || !allowListViewOption || phone ? (
                              <ProductPortfolioGrid
                                 data-testid="product-portfolio__grid"
                                 onPageChange={onPageChange}
                                 baseRoutes={baseRoutes}
                                 roleBasedToggles={roleBasedToggle}
                                 relatedDocumentsAnchor={relatedDocumentsAnchor}
                                 content={searchQuery.data.content}
                                 hasPublicLabelAndSigningsToggles={hasPublicLabelAndSigningsToggles}
                                 hasSustainabilityLabels={hasSustainabilityLabels}
                                 page={page}
                                 totalPages={searchQuery.data.totalPages}
                                 globals={globals}
                                 node={node}
                              />
                           ) : (
                              <ProductPortfolioTable
                                 unitSystem={unitSystem}
                                 data-testid="product-portfolio__table"
                                 activeFacets={activeFacets}
                                 roleBasedToggles={roleBasedToggle}
                                 baseRoutes={baseRoutes}
                                 content={searchQuery.data.content}
                                 hasPublicLabelAndSigningsToggles={hasPublicLabelAndSigningsToggles}
                                 allowShowPhyiscalProperties={allowShowPhysicalProperties}
                                 hasSustainabilityLabels={hasSustainabilityLabels}
                                 activeColumnsUpdate={onActiveColumnUpdate}
                                 activeColumns={columnSelection}
                                 onPageChange={onPageChange}
                                 page={page}
                                 totalPages={searchQuery.data.totalPages}
                                 globals={globals}
                              />
                           )}
                        </div>
                     )}
                  </GridCol>
               </Grid>
            )}
         </Box>

         {searchQuery.data && (
            <BreakpointSwitch excludeViews={['desktop']}>
               <Flyout open={filterFlyoutOpen} onFlyoutClose={() => setFilterFlyoutOpen(false)}>
                  {filters}
               </Flyout>
            </BreakpointSwitch>
         )}
      </>
   );
};
